/// <reference path="./disw.d.ts" />
import logger from './logger.js';
/** localStorage key for developer/QA feature flag overrides (non-prod only) */
const OVERRIDE_STORAGE_KEY = 'disw.featureFlags';
/** localStorage key for cached API flag values — updated on every successful fetch */
const CACHE_STORAGE_KEY = 'disw.featureFlags.cache';
/** Default fetch timeout in milliseconds */
const DEFAULT_TIMEOUT_MS = 500;
/** Module-level state: the resolved feature flag values for this session */
let _flags = {};
/** Module-level state: whether initialization has completed */
let _initialized = false;
/** Module-level state: the current environment (used to gate override behavior) */
let _env = 'prod';
/**
 * Determines the current environment using multiple detection strategies.
 * Since the features module initializes BEFORE the context module, we cannot
 * rely solely on window.disw.context.getEnv(). Detection order:
 *   1. window.disw.context.getEnv() (available if context initialized first in a re-init)
 *   2. process.env.UNIVERSAL_COMPONENTS_ENV (build-time env, same source other modules use)
 *   3. URL-based detection (localhost/dev/uat hostnames)
 *   4. Falls back to 'prod' (safest default — disables overrides)
 *
 * @returns The current environment string ('dev', 'uat', 'prod', etc.)
 */
function _resolveEnv() {
    // Strategy 1: Context module (may already be initialized on re-init)
    try {
        const contextEnv = window.disw?.context?.getEnv?.();
        if (contextEnv)
            return contextEnv;
    }
    catch {
        // context module may not be initialized yet — expected on first load
    }
    // Strategy 2: Build-time environment variable (used by localizations, context, region modules)
    try {
        const buildEnv = process.env.UNIVERSAL_COMPONENTS_ENV;
        if (buildEnv && buildEnv !== '')
            return buildEnv;
    }
    catch {
        // process.env may not exist in all browser environments
    }
    // Strategy 3: URL-based detection (reliable for local dev and known environments)
    try {
        const hostname = window.location?.hostname || '';
        if (hostname === 'localhost' || hostname === '127.0.0.1')
            return 'dev';
        if (hostname.includes('-dev') || hostname.includes('.dev.'))
            return 'dev';
        if (hostname.includes('-uat') || hostname.includes('.uat.') || hostname.includes('-qa'))
            return 'uat';
        if (hostname.includes('-staging') || hostname.includes('.staging.'))
            return 'staging';
    }
    catch {
        // window.location may not be available in SSR/test environments
    }
    return 'prod';
}
/**
 * Checks whether the current environment allows feature flag overrides.
 * Overrides are ONLY permitted in non-production environments to prevent
 * accidental testing artifacts from affecting real users.
 *
 * @returns true if overrides are allowed (dev, uat, feature, etc.), false if production
 */
function _canOverride() {
    return _env !== 'prod';
}
/**
 * Saves the current API-fetched flag values to localStorage.
 * Called on EVERY successful API fetch so localStorage always reflects the latest SSM state.
 * This ensures that if the API goes down on a subsequent page load, the user still has
 * the most recent SSM values as their fallback.
 *
 * @param flags - The flag values received from the API
 */
function _saveToCache(flags) {
    try {
        localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(flags));
    }
    catch {
        logger.warn('[features] Failed to save flags to localStorage cache.');
    }
}
/**
 * Loads the last-known-good flag values from localStorage cache.
 * Used as fallback when the API is unreachable. This represents the last successful
 * SSM state that was fetched — so flags stay consistent even during API outages.
 *
 * @returns Cached flag values, or null if no cache exists (first ever visit)
 */
function _loadFromCache() {
    try {
        const raw = localStorage.getItem(CACHE_STORAGE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
            return parsed;
        }
    }
    catch {
        logger.warn('[features] Failed to parse localStorage cache — ignoring.');
    }
    return null;
}
/**
 * Reads localStorage-based feature flag overrides set by developers/QA.
 * Returns an empty object if no overrides exist, if parsing fails, or if in production.
 * Overrides are the HIGHEST priority source — they win over API, cache, and defaults.
 * The cache (`disw.featureFlags.cache`) is NOT affected by overrides.
 *
 * Usage (in browser console, non-prod only):
 *   localStorage.setItem('disw.featureFlags', JSON.stringify({ 'expansion-enablement-si': false }))
 *
 * @returns A partial record of flag name → boolean overrides
 */
function _getLocalStorageOverrides() {
    if (!_canOverride())
        return {};
    try {
        const raw = localStorage.getItem(OVERRIDE_STORAGE_KEY);
        if (!raw)
            return {};
        const parsed = JSON.parse(raw);
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
            return parsed;
        }
    }
    catch {
        logger.warn('[features] Failed to parse localStorage overrides — ignoring.');
    }
    return {};
}
/**
 * Fetches feature flag values from the ecbe-middleware GET /feature-flags endpoint.
 * Applies a timeout to ensure page load is never blocked by a slow/unavailable API.
 *
 * @param apiUrl - The full URL to the feature flags endpoint
 * @param timeoutMs - Maximum time in ms to wait for the response
 * @returns The parsed flag values, or null if the fetch failed/timed out
 */
async function _fetchFlags(apiUrl, timeoutMs) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const response = await fetch(apiUrl, {
            method: 'GET',
            signal: controller.signal,
            headers: { 'Accept': 'application/json' },
        });
        clearTimeout(timeoutId);
        if (!response.ok) {
            logger.warn(`[features] API returned ${response.status} — falling back to defaults.`);
            return null;
        }
        const data = await response.json();
        // Validate response shape: must be a flat object of string → boolean
        if (typeof data !== 'object' || data === null || Array.isArray(data)) {
            logger.warn('[features] API returned malformed response — falling back to defaults.');
            return null;
        }
        // Coerce all values to booleans for safety
        const flags = {};
        for (const [key, value] of Object.entries(data)) {
            flags[key] = Boolean(value);
        }
        return flags;
    }
    catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
            logger.warn(`[features] API request timed out after ${timeoutMs}ms — falling back to defaults.`);
        }
        else {
            logger.warn(`[features] API request failed: ${error} — falling back to defaults.`);
        }
        return null;
    }
}
/**
 * Initializes the feature flags module.
 *
 * This function should be called FIRST in the disw-utils init() chain — before account,
 * country, and cart — so that flag values are available when components mount.
 *
 * SSM (via the ecbe-middleware API) is the single source of truth for all flag values.
 * No hardcoded defaults are needed — the backend self-seeds missing flags from its registry.
 *
 * Initialization flow:
 * 1. Resolve the current environment
 * 2. Fetch live values from the API (with timeout)
 * 3. If API fails, fall back to localStorage cache (last known good state)
 * 4. If no cache exists (first ever visit + API down), all flags default to false (fail-closed)
 * 5. Apply localStorage overrides (non-prod only, highest priority)
 * 6. Freeze the flags for the session (no mid-interaction UI changes)
 *
 * @example
 * ```typescript
 * await initializeFeatures({
 *     apiUrl: 'https://your-api-gateway.execute-api.us-east-2.amazonaws.com/ecbe/feature-flags',
 *     timeout: 500,
 * });
 * ```
 *
 * @param config - Configuration object with apiUrl and optional timeout
 * @param config.apiUrl - The full URL to the GET /feature-flags endpoint
 * @param config.timeout - Max milliseconds to wait for the API (default: 500)
 * @param config.env - Optionally specify environment for override gating (auto-detected if omitted)
 */
export async function initializeFeatures(config) {
    const { apiUrl, timeout = DEFAULT_TIMEOUT_MS, env } = config;
    // Resolve environment (needed for override gating).
    // Prefer explicitly passed env (since features inits before context module).
    _env = env || _resolveEnv();
    // Step 1: Attempt to fetch live flag values from the API (SSM is the single source of truth)
    const liveFlags = await _fetchFlags(apiUrl, timeout);
    if (liveFlags) {
        // API success: use live values directly
        _flags = { ...liveFlags };
        // Persist to localStorage so we have a fallback if the API is down next time
        _saveToCache(liveFlags);
        logger.log('[features] Loaded live feature flags from API (cached to localStorage).');
    }
    else {
        // API failed: try localStorage cache (last known good SSM state)
        const cached = _loadFromCache();
        if (cached) {
            _flags = { ...cached };
            logger.log('[features] API unavailable — using cached flag values from localStorage.');
        }
        else {
            // No cache exists (first ever visit + API down) — fail closed (all flags false)
            _flags = {};
            logger.warn('[features] API unavailable, no cache — all flags will return false (fail-closed).');
        }
    }
    // Step 3: Apply localStorage overrides (non-prod only — highest priority when active)
    const overrides = _getLocalStorageOverrides();
    if (Object.keys(overrides).length > 0) {
        _flags = { ..._flags, ...overrides };
        logger.log(`[features] Applied ${Object.keys(overrides).length} localStorage override(s).`);
    }
    _initialized = true;
    logger.log(`[features] Initialization complete. Flags: ${JSON.stringify(_flags)}`);
}
/**
 * Returns whether a named feature flag is currently enabled.
 *
 * If the features module has not been initialized, logs a warning and returns false (fail-closed).
 * If the requested flag name does not exist in the resolved flags, returns false.
 *
 * @example
 * ```typescript
 * if (window.disw.features.isEnabled('expansion-enablement-si')) {
 *     // Show expansion add-ons UI for SI subscriptions
 * }
 * ```
 *
 * @param flagName - The kebab-case name of the feature flag (e.g., 'expansion-enablement-si', 'update-po-number-si')
 * @returns true if the flag is enabled, false if disabled or unknown
 */
export function isFeatureEnabled(flagName) {
    if (!_initialized) {
        logger.warn(`[features] isEnabled('${flagName}') called before initialization — returning false.`);
        return false;
    }
    return _flags[flagName] ?? false;
}
/**
 * Returns a shallow copy of all current feature flag values.
 * Useful for debugging or logging the full flag state.
 *
 * @returns A record of all flag names mapped to their current boolean values
 */
export function getAllFeatures() {
    return { ..._flags };
}
/**
 * Sets a feature flag value, applies it immediately to the current session,
 * AND persists it to localStorage so it survives page refreshes.
 * Only works in non-production environments. In production, this is a no-op.
 *
 * This is the recommended way to toggle flags during testing — no refresh needed.
 *
 * @example
 * ```javascript
 * // In browser console:
 * disw.features.set('expansion-enablement-si', false)     // disable SI expansion
 * disw.features.set('expansion-enablement-si', true)      // enable SI expansion
 * ```
 *
 * @param flagName - The kebab-case name of the flag to set
 * @param value - The boolean value to set (true = enabled, false = disabled)
 */
export function setFeature(flagName, value) {
    if (!_canOverride()) {
        logger.warn('[features] Overrides are not allowed in production.');
        return;
    }
    _flags[flagName] = value;
    _persistOverride(flagName, value);
    logger.log(`[features] ${flagName} = ${value} (applied + persisted).`);
}
/**
 * Shorthand to enable a feature flag. Applies immediately and persists.
 * Only works in non-production environments.
 *
 * @example
 * ```javascript
 * disw.features.enable('expansion-enablement-si')
 * ```
 *
 * @param flagName - The kebab-case name of the flag to enable
 */
export function enableFeature(flagName) {
    setFeature(flagName, true);
}
/**
 * Shorthand to disable a feature flag. Applies immediately and persists.
 * Only works in non-production environments.
 *
 * @example
 * ```javascript
 * disw.features.disable('expansion-enablement-si')
 * ```
 *
 * @param flagName - The kebab-case name of the flag to disable
 */
export function disableFeature(flagName) {
    setFeature(flagName, false);
}
/**
 * Toggles a feature flag's current value. Applies immediately and persists.
 * Only works in non-production environments.
 *
 * @example
 * ```javascript
 * disw.features.toggle('expansion-enablement-si')  // if true → false, if false → true
 * ```
 *
 * @param flagName - The kebab-case name of the flag to toggle
 */
export function toggleFeature(flagName) {
    const current = _flags[flagName] ?? false;
    setFeature(flagName, !current);
}
/**
 * Temporarily overrides a feature flag value for the current session.
 * Only works in non-production environments. In production, this is a no-op.
 *
 * The override is NOT persisted to localStorage — it only lasts until the next page refresh.
 * For persistent overrides (across refreshes), use set() instead.
 *
 * @param flagName - The name of the flag to override
 * @param value - The boolean value to set
 */
export function overrideFeature(flagName, value) {
    if (!_canOverride()) {
        logger.warn('[features] Overrides are not allowed in production.');
        return;
    }
    _flags[flagName] = value;
    logger.log(`[features] Override applied: ${flagName} = ${value} (ephemeral, until refresh).`);
}
/**
 * Removes all localStorage-based feature flag overrides and resets flags
 * to their API/default values. Only works in non-production environments.
 * After calling this, the next page load will use API values (or defaults) without overrides.
 */
export function clearOverrides() {
    if (!_canOverride()) {
        logger.warn('[features] Overrides are not allowed in production.');
        return;
    }
    try {
        localStorage.removeItem(OVERRIDE_STORAGE_KEY);
        logger.log('[features] All localStorage overrides cleared. Refresh to apply.');
    }
    catch {
        logger.warn('[features] Failed to clear localStorage overrides.');
    }
}
/**
 * Persists a single flag override to localStorage, merging with any existing overrides.
 * Internal helper used by set(), enable(), disable(), and toggle().
 *
 * @param flagName - The flag name to persist
 * @param value - The boolean value to persist
 */
function _persistOverride(flagName, value) {
    try {
        const existing = _getLocalStorageOverrides();
        existing[flagName] = value;
        localStorage.setItem(OVERRIDE_STORAGE_KEY, JSON.stringify(existing));
    }
    catch {
        logger.warn('[features] Failed to persist override to localStorage.');
    }
}
/**
 * Creates the public features module object to be exposed on window.disw.features.
 * This follows the same module pattern used by other disw-utils modules (cart, context, etc.).
 *
 * @returns The FeaturesModule object with isEnabled, getAll, override, and clearOverrides methods
 */
function _createFeaturesModule() {
    return {
        isEnabled: isFeatureEnabled,
        getAll: getAllFeatures,
        set: setFeature,
        enable: enableFeature,
        disable: disableFeature,
        toggle: toggleFeature,
        override: overrideFeature,
        clearOverrides,
    };
}
export default _createFeaturesModule();
